/**
 * 06 报名详情 `/registration/:registrationId`（含 11 驳回态）
 * -----------------------------------------------------------------------------
 * 版面按设计稿「学员端 · 报名详情」：渐变状态头 → 办理进度（时间轴）→
 * 报名信息 → 底部两键；驳回态换成红色状态头 + 驳回原因 + 重提须知。
 *
 * - 状态时间轴（创建 / 提交 / 报名成功 / 最近更新）
 * - 表单**按报名单绑定的那一版 Schema 回显**
 * - 驳回态：给出重提入口（驳回原因服务端未在详情视图下发，见交付说明）
 * - 发票区块（C-25）：申请 / 待开具 / 已开具 / 已驳回+重新申请，四态互斥穷尽
 *
 * 契约缺口：服务端没有「按 `schemaId + schemaVersion` 取历史版本」的学员端端点
 * （`GET /api/h5/forms/{enrollTypeId}` 只返回最新已发布版本），因此当最新版本号
 * 与报名单绑定的版本不一致时，**仍按表单里实际存下的值回显**，但字段名与选项文案
 * 借用最新版本翻译（口径见 `RegistrationDetail.logic.ts`）—— 既不丢内容，也不印
 * 英文键和原始枚举值。
 */
import {
  DeliveryMethod,
  type FormSchemaView,
  formatRegionText,
  isInvoiceableRegistrationStatus,
  MATERIAL_STATUS_LABEL,
  MaterialStatus,
  type RegistrationStatus,
} from '@enroll/shared';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormSchema, useRegistration } from '../api/hooks';
import type { RegistrationDetailView } from '../api/types';
import { formatCentsText } from '../components/FormRenderer/logic';
import { InvoicePanel } from '../components/InvoicePanel/InvoicePanel';
import { AlertIcon, CheckIcon } from '../components/icons';
import {
  Button,
  Card,
  CourseStatusTag,
  ErrorBlock,
  KV,
  KVImage,
  LoadingBlock,
  Notice,
  PageShell,
  StatusTag,
} from '../components/layout';
import { formatDateTime } from '../lib/datetime';
import { buildAnswerSections, maskAddressPhone } from './RegistrationDetail.logic';

interface TimelineEntry {
  key: string;
  title: string;
  description: string;
}

function buildTimeline(data: RegistrationDetailView): TimelineEntry[] {
  // 时间必须格式化后再上屏：服务端统一下发 ISO，直接渲染会露出 `T`/时区尾巴。
  // 只列**流程节点**，不放「最近更新」这类内部字段 —— 那不是用户认知里的一步。
  const entries: TimelineEntry[] = [
    { key: 'created', title: '填写报名信息', description: formatDateTime(data.createdAt) },
  ];
  if (data.submittedAt)
    entries.push({
      key: 'submitted',
      title: '提交报名',
      description: formatDateTime(data.submittedAt),
    });
  if (data.enrolledAt)
    entries.push({
      key: 'enrolled',
      title: '报名成功',
      description: formatDateTime(data.enrolledAt),
    });
  return entries;
}

/** 状态头文案：一句话说清当前处境 */
function heroOf(status: RegistrationStatus): { title: string } {
  switch (status) {
    case 'ENROLLED':
      return { title: '恭喜，你已完成全部报名流程' };
    case 'PENDING_PAYMENT':
      return { title: '待缴费，请完成转账' };
    case 'REJECTED':
      return { title: '材料需要修改后重新提交' };
    case 'DRAFT':
      return { title: '报名还在填写中' };
    case 'REFUNDED':
      return { title: '该报名单已退费' };
    case 'VOIDED':
      return { title: '该报名单已作废' };
    default:
      return { title: '报名单已提交' };
  }
}

/**
 * 表单填写内容：**按 Schema 分组出卡**，未填字段也出列（写「未填写」）。
 *
 * 之前这里三条毛病：未填字段整行消失（学员以为内容丢了）、`region` 打成
 * `[object Object]`、版本漂移时直接印英文键与原始枚举值。取值与排版口径都挪到了
 * `RegistrationDetail.logic.ts`，那边有单测。
 */
function AnswerSections({
  data,
  schema,
  versionMatched,
  hiddenKeys,
}: {
  data: RegistrationDetailView;
  schema: FormSchemaView | undefined;
  versionMatched: boolean;
  hiddenKeys: readonly string[];
}) {
  const sections = useMemo(
    () =>
      buildAnswerSections({
        formData: data.formData,
        signatureUrls: data.signatureUrls,
        schema,
        versionMatched,
        hiddenKeys,
      }),
    [data.formData, data.signatureUrls, schema, versionMatched, hiddenKeys],
  );

  if (sections.length === 0) {
    return (
      <Card title="表单填写内容" tight>
        <p className="card__desc">尚未填写表单内容</p>
      </Card>
    );
  }

  return (
    <>
      {sections.map((section) => (
        <Card key={section.key} title={section.title || '表单填写内容'} tight>
          {section.rows.map((row) =>
            row.imageUrl ? (
              <KVImage key={row.key} label={row.label} url={row.imageUrl} alt="手写签名" />
            ) : (
              <KV key={row.key} label={row.label} value={row.value} />
            ),
          )}
        </Card>
      ))}
    </>
  );
}

export default function RegistrationDetail() {
  const navigate = useNavigate();
  const { registrationId } = useParams<{ registrationId: string }>();
  const detail = useRegistration(registrationId);
  const published = useFormSchema(detail.data?.enrollTypeId);

  /*
    等**两个**查询都落地再画：只等报名单会让表单内容先按「无 Schema」渲染一帧
    （英文键 + 原始枚举值），再被正确的标签替换掉 —— 那一瞬间的闪烁正是
    「显示不正确」的观感来源。`useFormSchema` 的入参为空时是禁用态（`isPending`
    恒 true），所以这里**必须**带上 `registrationId` 判断，否则没有路由参数时
    会永远转圈而不是给出「报名单不存在」。
  */
  const settle = Boolean(registrationId) && (detail.isPending || published.isPending);
  if (settle) {
    return (
      <PageShell title="报名详情" back>
        <LoadingBlock text="正在加载报名详情" />
      </PageShell>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <PageShell title="报名详情" back>
        <ErrorBlock
          message={detail.error instanceof Error ? detail.error.message : '报名单不存在'}
          onRetry={() => {
            void detail.refetch();
          }}
        />
      </PageShell>
    );
  }

  const data = detail.data;
  const versionMatched = Boolean(published.data && published.data.version === data.schemaVersion);
  const timeline = buildTimeline(data);
  /*
    补交态（2026-10-27）：运营判定「资料需补充」**不改状态**（
    `judgeSupplement` 不产生流转），所以单子可能还停在 `PENDING_PAYMENT` ——
    只看 `status` 判断不出来，必须看这次判定留下的两样东西。
      - `supplementMissingItems` 非空 = 有「要补什么」的清单
      - `grantedFields` 非空 = 后台放开了几个 `review` 字段让学员自己改
    两者任一都算补交态：只放行不放缺项的情形（例如「重新上传一张清楚的证书」）
    清单里可能没有条目，但学员确实需要进去改。
  */
  const hasSupplement = data.supplementMissingItems.length > 0 || data.grantedFields.length > 0;
  // 补交态下 `PENDING_PAYMENT` 的单子也要能进表单 —— 没有这个分支，学员点进
  // 「补充资料」只会看到一个只读表单（原实现只有 DRAFT / REJECTED 可编辑）
  const canEdit = data.status === 'DRAFT' || data.status === 'REJECTED' || hasSupplement;
  const rejected = data.status === 'REJECTED';
  // 教材状态是**独立列**（2026-10-22 起），字段缺失时按「待申请领取」
  const materialStatus =
    (data.materialStatus as MaterialStatus | undefined) ?? MaterialStatus.PENDING_APPLY;
  const hero = heroOf(data.status);

  /*
    上方「报名信息」卡已经列过的系统字段，在表单卡里不再重复 —— 但**只在上面
    真的有值时才去重**：服务端没下发 `applicantName` 的老单子，表单里的 `name`
    就是唯一来源，隐藏它就等于丢内容。
  */
  const duplicatedKeys = [
    data.applicantName ? 'name' : '',
    data.idCardNo || data.idCardMasked ? 'idCardNo' : '',
    data.phoneMasked ? 'phone' : '',
  ].filter(Boolean);

  /*
    驳回态是**另一套版面**（设计稿第 11 块）：
    没有渐变状态头，改成「审核未通过」提示条 + 驳回说明 + 报名信息 + 重新提交须知，
    页脚也从上下两排换成左右两键 —— 这一态的动线是「看完原因立刻去改」。
  */
  if (rejected) {
    return (
      <PageShell
        title="报名详情"
        back
        layout="detail"
        footer={
          <div className="btn-row">
            <Button variant="ghost" onClick={() => navigate('/help/service')}>
              联系客服
            </Button>
            <Button variant="primary" onClick={() => navigate(`/enroll/${data.id}`)}>
              重新提交材料
            </Button>
          </div>
        }
      >
        <div className="notice-card notice-card--danger">
          <span className="notice-card__title">
            <AlertIcon size={15} strokeWidth={2} />
            审核未通过
          </span>
          <span className="notice-card__meta">
            第 {data.rejectCount} 次驳回 · {formatDateTime(data.updatedAt)}
          </span>
        </div>

        {/*
          驳回原因必须是**运营真填的那句话**（2026-10-27）。这里原先是硬编码文案
          「提交的证件或证件照未通过身份核验……具体扣分项已通过公众号消息推送」——
          而 T1 短信让学员「登录学员中心查看原因」，页面却只给一段通用话术，
          真实原因（如「学历证书不清晰」）学员永远看不到。
        */}
        <Card title="驳回原因" tight>
          <p className="card__desc">
            {data.latestRejection?.reason ?? '管理员未填写具体原因，请联系客服查询后再重新提交。'}
          </p>
          {data.latestRejection ? (
            <KV label="驳回时间" value={formatDateTime(data.latestRejection.createdAt)} />
          ) : null}
        </Card>

        <Card title="报名信息" tight>
          <KV label="报名类型" value={data.enrollTypeName} lined />
          <KV label="报名批次" value={data.batchName} lined />
          <KV
            label="报名单号"
            value={<span className="mono-num">{data.registrationNo}</span>}
            lined
          />
          <KV
            label="应缴金额"
            value={<span className="mono-num">{formatCentsText(data.payableCents)}</span>}
            lined
          />
        </Card>

        <Card title="重新提交须知" tight>
          <ul className="bullet-list">
            <li className="bullet-list__item">
              <span className="bullet-list__dot" />
              请先按上面的驳回原因逐项核对，只改需要改的部分
            </li>
            <li className="bullet-list__item">
              <span className="bullet-list__dot" />
              证件照需四角完整、无遮挡、无反光
            </li>
            <li className="bullet-list__item">
              <span className="bullet-list__dot" />
              单张照片不超过 5MB，仅支持 JPG / PNG
            </li>
            <li className="bullet-list__item">
              <span className="bullet-list__dot" />
              请勿使用截图、翻拍或美颜处理过的照片
            </li>
            <li className="bullet-list__item">
              <span className="bullet-list__dot" />
              重新提交后系统即时重新核验，无需等待人工审核
            </li>
          </ul>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="报名详情"
      back
      layout="detail"
      footerRows={2}
      footer={
        <>
          {/* 设计稿页脚是「主操作在上、次操作在下」的两排 46 全圆按钮。
              补交态把「补充资料」提到主位（这是运营要求学员做的下一件事），
              缴费降为次排 —— 两件事都要能做，不能因为要补资料就把缴费入口挤掉 */}
          {hasSupplement ? (
            <Button
              variant="primary"
              block
              className="btn--bar"
              onClick={() => navigate(`/enroll/${data.id}`)}
            >
              补充资料
            </Button>
          ) : canEdit ? (
            <Button
              variant="primary"
              block
              className="btn--bar"
              onClick={() => navigate(`/enroll/${data.id}`)}
            >
              继续填写
            </Button>
          ) : data.status === 'PENDING_PAYMENT' ? (
            <Button
              variant="primary"
              block
              className="btn--bar"
              onClick={() => navigate(`/payment/${data.id}`)}
            >
              去缴费
            </Button>
          ) : (
            <Button variant="primary" block className="btn--bar" onClick={() => navigate('/home')}>
              返回服务台
            </Button>
          )}
          {hasSupplement && data.status === 'PENDING_PAYMENT' ? (
            <div className="btn-row">
              <Button variant="ghost" onClick={() => navigate(`/payment/${data.id}`)}>
                去缴费
              </Button>
              <Button variant="ghost" onClick={() => navigate('/help/service')}>
                联系客服
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              block
              className="btn--bar"
              onClick={() => navigate('/help/service')}
            >
              联系客服
            </Button>
          )}
        </>
      }
    >
      <div className="detail-hero">
        <div className="detail-hero__top">
          <span className="pill-row">
            <StatusTag status={data.status} ghost />
            {/* 开课状态是**独立维度**（C-22）：它由管理员单独设置，报名成功后
                仍可与主状态并存，故在状态头并列展示。 */}
            <CourseStatusTag status={data.courseStatus} />
          </span>
          <span className="detail-hero__batch">
            {data.batchName} · {data.enrollTypeName}
          </span>
        </div>
        <div className="detail-hero__title">{hero.title}</div>
        <div className="detail-hero__desc">
          <span className="mono-num">报名单号 {data.registrationNo}</span>
          {data.status === 'ENROLLED' ? ' · 教材将于 3 个工作日内寄出' : ''}
          {data.status === 'PENDING_PAYMENT' ? ` · 应缴 ${formatCentsText(data.payableCents)}` : ''}
        </div>
      </div>

      {/*
        补交提示放在缴费提示**之前**：缴费是可以随时做的常规动作，补资料是
        运营卡着一个时限在等的东西。两件事同时存在时，先看到要紧的那件。

        ⚠️ 这里以前不存在：「资料需补充」的判定只写进了通知 payload 与审计表，
        报名单上没有任何字段，页面自然也无从显示 —— T5 短信却明确说
        「请登录学员中心查看缺项并重新上传」，承诺的是界面上没有的功能。
      */}
      {hasSupplement ? (
        <div className="notice-card notice-card--warning">
          <span className="notice-card__title">
            <AlertIcon size={15} strokeWidth={2} />
            资料需要补充
          </span>
          <span className="notice-card__meta">
            {data.supplementReason ?? '按下方清单补充材料后保存即可'}
          </span>
        </div>
      ) : null}

      {hasSupplement && data.supplementMissingItems.length > 0 ? (
        <Card title="待补项" tight>
          <ul className="bullet-list">
            {data.supplementMissingItems.map((item) => (
              <li key={item} className="bullet-list__item">
                <span className="bullet-list__dot" />
                {item}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {data.status === 'PENDING_PAYMENT' ? (
        <Notice tone="warning">
          待缴费：应缴 {formatCentsText(data.payableCents)}，请按缴费指引转账并在备注中填写备注码。
        </Notice>
      ) : null}

      <Card title="办理进度" tight>
        <div className="timeline">
          {timeline.map((entry, index) => {
            const isLast = index === timeline.length - 1;
            return (
              <div key={entry.key} className="timeline__item">
                <div className="timeline__marker">
                  <span className={`timeline__dot${isLast ? ' timeline__dot--current' : ''}`}>
                    <CheckIcon size={10} strokeWidth={2.6} />
                  </span>
                </div>
                <div className="timeline__body">
                  <span className={`timeline__title${isLast ? ' timeline__title--current' : ''}`}>
                    {entry.title}
                  </span>
                  <span className="timeline__meta mono-num">{entry.description}</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* 设计稿的「报名信息」卡紧接在办理进度之后，固定四行核心身份信息。
          完整表单内容（含业务字段）另起一张卡放在末尾，避免与这里的四行读起来重复。 */}
      <Card title="报名信息" tight>
        <KV label="姓名" value={data.applicantName ?? '—'} />
        <KV label="身份证号" value={data.idCardNo ?? data.idCardMasked ?? '—'} />
        {/* 服务端 `phoneMasked` 为空时**不出这一行**：手机号是表单里的必填项，
            下面「基本信息」卡一定有值，这里印一行「—」只会让人以为号码丢了。 */}
        {data.phoneMasked ? <KV label="手机号" value={data.phoneMasked} /> : null}
        <KV label="报名类型" value={`${data.enrollTypeName} · ${data.batchName}`} />
      </Card>

      {/*
        教材：只展示**一个派生状态**（C-22 独立维度，不可直接编辑）。
        一期不接物流商系统 —— 地址由后台导出后人工交快递，故学员端**不展示运单号**，
        只回答「教材发了没有」这一个问题（2026-10-17 承接方拍板）。
      */}
      <Card title="教材" tight>
        <KV
          label="领取方式"
          value={data.deliveryMethod === DeliveryMethod.PICKUP ? '现场领取' : '邮寄'}
        />
        <KV label="发放状态" value={MATERIAL_STATUS_LABEL[materialStatus]} />
        {/*
          邮寄地址：服务端在详情视图里已经下发（`address`），此前页面从不渲染，
          学员看不到「寄到哪儿」。一期不接物流商系统、学员端不展示运单号，但
          **寄送地址本人才是最终确认人** —— 填错了要在这里能看出来并去改。
          现场领取时地址无意义，不展示。
        */}
        {data.deliveryMethod !== DeliveryMethod.PICKUP && data.address ? (
          <>
            <KV
              label="收件人"
              value={`${data.address.receiverName} ${maskAddressPhone(data.address.phone)}`}
            />
            <KV label="邮寄地址" value={formatRegionText(data.address) || '—'} />
          </>
        ) : null}
      </Card>

      <Card title="费用" tight>
        {data.priceItems.length === 0 ? <KV label="分项报价" value="暂无" /> : null}
        {data.priceItems.map((item) => (
          <KV
            key={item.name}
            label={item.name}
            value={<span className="mono-num">{formatCentsText(item.amountCents)}</span>}
          />
        ))}
        {data.reductionCents > 0 ? (
          <KV
            label="减免"
            value={<span className="mono-num">-{formatCentsText(data.reductionCents)}</span>}
          />
        ) : null}
        <KV
          label="应缴合计"
          value={
            <span className="mono-num amount-strong">{formatCentsText(data.payableCents)}</span>
          }
          lined
        />
      </Card>

      {/* 发票区块（C-25）。可申请与否由共享判定函数决定 —— 与服务端同一份口径，
          服务端收紧后这里会自动跟着收起表单，不会留下一个提交必被拒的入口。 */}
      <InvoicePanel
        registrationId={data.id}
        canApply={isInvoiceableRegistrationStatus(data.status)}
        applicantName={data.applicantName}
      />

      {/*
        版本不一致不再说「按字段键值回显」——那是给开发看的行话，学员只会以为
        自己填的东西出了错。这里如实说清：内容仍然是你填的那些，只是项目名称
        可能随最新表单变化（label 已经按最新 Schema 翻译过）。
      */}
      {!versionMatched && published.data ? (
        <Notice tone="info">
          表单项目已更新，以下仍按你提交时填写的内容展示；个别项目名称可能与最新表单略有不同。
        </Notice>
      ) : null}

      <AnswerSections
        data={data}
        schema={published.data}
        versionMatched={versionMatched}
        hiddenKeys={duplicatedKeys}
      />
    </PageShell>
  );
}
